export interface ServiceAreaCity {
  name: string;
  slug: string;
  description: string;
  heroImage: string;
  localInfo: {
    about: string;
    landmarks: string[];
    neighborhoods?: string[];
  };
  intro?: string;
  /**
   * What the housing stock in this city is actually like — build eras,
   * roof shapes, common siding. This is the section that makes a city page
   * genuinely different from its neighbours instead of swapped-name filler,
   * which is what Google rewards.
   */
  housingStock?: string;
  /** Two to four exterior problems specific to this city's homes. */
  exteriorChallenges?: { title: string; body: string }[];
  /** Only set where real published projects back it up. Never invent these. */
  localProof?: string;
  faqs?: { question: string; answer: string }[];
}

export const serviceAreaCities: ServiceAreaCity[] = [
  {
    name: "Minneapolis",
    slug: "minneapolis",
    description:
      "Full roofing, siding, and exterior services for Minneapolis homeowners.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Minneapolis, the largest city in Minnesota, is known for its beautiful lakes, vibrant arts scene, and thriving neighborhoods. From the historic homes of Kenwood to the modern developments in North Loop, we've been helping Minneapolis homeowners protect their investments for over 20 years.",
      landmarks: [
        "Lake Harriet",
        "Minneapolis Sculpture Garden",
        "Stone Arch Bridge",
        "Minnehaha Falls",
        "Lake of the Isles",
      ],
      neighborhoods: [
        "Uptown",
        "North Loop",
        "Linden Hills",
        "Southwest Minneapolis",
        "Northeast",
      ],
    },
    housingStock:
      "Most of the Minneapolis housing we work on was built between roughly 1900 and 1940. That means steep pitches, cut-up rooflines with multiple dormers and valleys, real masonry chimneys, and a lot of stucco, often paired with wood siding on upper gables. Detached garages off the alley are close to universal and are frequently in worse shape than the house because they are out of sight. Lots are narrow, homes sit close together, and there is rarely a convenient place to put a dumpster. All of that changes the logistics of a tear-off compared with an Andover two-storey on a half-acre lot.",
    exteriorChallenges: [
      {
        title: "Steep pitches and multiple layers",
        body: "Century-old Minneapolis roofs are steep enough to need extra staging and fall protection, and many carry two layers already. That combination means a proper job here costs more than the same square footage in the suburbs, and a quote that looks unusually cheap is usually planning a layover or skipping safety setup.",
      },
      {
        title: "Dormers, valleys and sidewall transitions",
        body: "The character that makes these homes attractive is also what leaks. Dormer cheeks, valley intersections and the junctions where a roof plane dies into a stucco wall need step and kick-out flashing done correctly. On stucco especially, a missing kick-out flashing sends water straight behind the wall where you will not see it until there is real damage.",
      },
      {
        title: "Alley access, tight lots and neighbours",
        body: "There is often nowhere to park a dumpster except the alley or the street, and the next house may be fifteen feet away. Protecting the neighbouring property, sequencing deliveries and doing a genuinely thorough magnet sweep matter more here than anywhere else we work. It is worth asking any contractor how they plan to stage the job before they start.",
      },
      {
        title: "Detached garages that get forgotten",
        body: "The garage off the alley is usually older, smaller and further gone than the house. Doing it at the same time as the main roof is far cheaper per square than a separate mobilisation later, and it is the single most common thing we get asked to add once we are already on site.",
      },
    ],
    intro:
      "Minneapolis is a different job from the suburbs and we price and plan it that way. The housing is older, the roofs are steeper, the lots are tighter and access is often through an alley rather than a driveway. We work across the city on roofing, siding and gutters, and the details that matter here are the ones that come with turn-of-the-century construction and dense city lots.",
    faqs: [
      {
        question: "Do you work in Minneapolis proper, not just the suburbs?",
        answer:
          "Yes. We work across Minneapolis on roofing, siding and gutter projects. City homes need a different approach from suburban ones because of the age, the pitch and the access, and we plan for that rather than treating it as a suburban job in a tighter spot.",
      },
      {
        question:
          "My Minneapolis home is steep and has several dormers. Does that cost more?",
        answer:
          "It does, and it should. Steep pitches need extra staging and fall protection, and every dormer and valley adds flashing detail and labour. We give you an itemised estimate so you can see where the cost sits rather than a lump sum that hides it.",
      },
      {
        question: "Where will the dumpster go on a narrow city lot?",
        answer:
          "We work that out before the job starts, usually the alley, the driveway or a permitted street placement depending on your block. We also protect the neighbouring property and finish with a full magnet sweep, which matters more on tight lots than anywhere else.",
      },
      {
        question: "Can you do my detached alley garage at the same time?",
        answer:
          "Yes, and it is usually the smart move. Doing the garage alongside the house costs far less per square than bringing a crew back for it separately, and on most Minneapolis properties the garage is in worse shape than the house.",
      },
      {
        question: "My home is stucco. Can you still do the roof and gutters?",
        answer:
          "Yes. Stucco homes need particular care where roof planes meet walls, especially kick-out flashing at the bottom of a roof-to-wall junction. Missing that detail is one of the most common causes of hidden water damage behind stucco, and it is something we check on every stucco home.",
      },
      {
        question: "Do you help with insurance claims in Minneapolis?",
        answer:
          "Yes. We document the damage in full, meet your adjuster on site, and make sure the scope reflects the whole exterior rather than shingles alone. Gutters, wraps, vents and ridge are commonly left off a first estimate.",
      },
      {
        question: "What other areas do you serve near Minneapolis?",
        answer:
          "We work throughout the north metro including Fridley, Brooklyn Park, Coon Rapids, Blaine, Anoka, Andover and Ham Lake, along with Minneapolis itself.",
      },
    ],
  },
  {
    name: "Anoka",
    slug: "anoka",
    description: "Trusted local contractor serving Anoka County residents.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Anoka, known as the 'Halloween Capital of the World,' is a charming city with deep roots in Minnesota history. Located along the scenic Rum River, Anoka features beautiful historic homes and growing new developments. We're proud to have served Anoka homeowners for over 20 years.",
      landmarks: [
        "Rum River",
        "Anoka County Fairgrounds",
        "Colonial Hall",
        "Downtown Historic District",
        "Father Hennepin State Park",
      ],
      neighborhoods: [
        "Downtown Anoka",
        "Sunrise",
        "North Anoka",
        "South Anoka",
      ],
    },
    intro:
      "Anoka's historic character is part of its charm—and a factor in its roofs. Along the Rum River you'll find older and historic homes, some with steep, complex, or multi-gable rooflines that take extra care to replace correctly, alongside newer developments on the city's edges. We've worked on Anoka's older housing stock for over 20 years and know how to protect these homes through Minnesota storms.",
    housingStock:
      "Anoka is the county seat and it carries the oldest housing stock in the area. Around the downtown historic district and along the Rum River you will find homes from the late 1800s through the 1940s: steep pitches, front gables, dormers, wood soffit and fascia, and the occasional slate or cedar roof that was covered with asphalt decades ago. Those roofs were framed for steep, fast-shedding designs, which is good, but they are also cut up with valleys and chimney junctions and often carry two or three layers by now. Further out toward Sunrise and the newer edges of the city you get standard post-war and late-century ramblers and two-storeys, so a single Anoka street can jump fifty years in build date.",
    exteriorChallenges: [
      {
        title: "Steep, layered roofs on the older homes",
        body: "A steep historic roof is slower and more dangerous to work on, which is why some contractors quietly quote a layover instead of a tear-off. Minnesota code caps you at two layers, and older Anoka homes are frequently already there. Layer count changes the price, the disposal and the plan, so it is one of the first things we check rather than something we discover mid-job.",
      },
      {
        title: "Chimney and masonry flashing",
        body: "Older Anoka homes often have real masonry chimneys, and the flashing around them is where leaks start. Tar smeared over a failing counterflashing is a common sight and a temporary fix at best. Proper step and counterflashing cut into the mortar joint is the repair that lasts, and it is worth confirming any estimate includes it rather than a caulk-and-hope.",
      },
      {
        title: "Wood soffit, fascia and trim rot",
        body: "Decades of overflowing gutters take their toll on wood trim, and on historic homes the rot is usually found only once the gutters come off. We check the fascia condition before quoting so any carpentry is in the estimate up front instead of arriving as a surprise change order.",
      },
      {
        title: "Matching the look on a historic street",
        body: "On the older blocks the goal is usually a roof and siding that read correctly for the house rather than the cheapest option available. Architectural shingles in the right colour and profile, and siding with an appropriate reveal, make a large difference on a home with period detailing.",
      },
    ],
    faqs: [
      {
        question: "Do you offer free estimates in Anoka?",
        answer:
          "Yes—free, no-obligation estimates for every Anoka homeowner, with a clear written quote and no pressure.",
      },
      {
        question:
          "How quickly can you respond to storm or hail damage in Anoka?",
        answer:
          "Anoka is right in our home territory, so we respond fast after storms—prioritizing inspections and documenting hail or wind damage for your claim.",
      },
      {
        question: "Do you help Anoka homeowners with insurance claims?",
        answer:
          "Yes. We document storm damage, meet your adjuster on site, and make sure the scope is complete—we've guided Anoka homeowners through claims for two decades.",
      },
      {
        question: "Do you work on older and historic Anoka homes?",
        answer:
          "We do. Many Anoka homes have steeper or more complex rooflines and older decking that need extra attention. We replace any damaged sheathing and install a full modern system while respecting the home's character.",
      },
      {
        question: "Are ice dams a problem on Anoka's older homes?",
        answer:
          "Older homes often have less attic insulation and ventilation, which makes ice dams more likely. We fix the damage and improve insulation, air sealing, and ventilation to prevent them.",
      },
      {
        question: "What siding suits historic Anoka homes?",
        answer:
          "We help match siding to your home's style—LP SmartSide gives the look of real wood with far better durability, while James Hardie offers premium, long-lasting protection.",
      },
      {
        question: "What areas near Anoka do you serve?",
        answer:
          "Anoka plus Andover, Ramsey, Coon Rapids, Champlin, and the surrounding area.",
      },
    ],
  },
  {
    name: "Andover",
    slug: "andover",
    description: "Quality roof replacements and siding for Andover homes.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Andover is one of Minnesota's fastest-growing cities, blending rural charm with suburban convenience. With its excellent schools and family-friendly neighborhoods, Andover has become a premier destination for families. We've been proud to serve this growing community for over 20 years.",
      landmarks: [
        "Bunker Hills Regional Park",
        "Andover YMCA",
        "Kelsey Round Lake Park",
        "Prairie Oaks Dog Park",
        "Rum River North Regional Park",
      ],
      neighborhoods: [
        "Bunker Hills",
        "Oak Grove",
        "Crosstown",
        "Woodland Trails",
      ],
    },
    intro:
      "Andover is one of Anoka County's fastest-growing cities, with many larger homes built from the 1990s through the 2010s—which means a wave of them are now hitting the age where builder-grade shingles wear out. Bigger homes here often have complex rooflines with multiple valleys and dormers, and like the rest of the county, Andover sees real hail and snow. We've served this growing community for over 20 years.",
    housingStock:
      "Andover built out later than most of Anoka County, mainly from the 1990s through the 2010s, and it built bigger. Two-storey homes on larger lots are the norm, and the rooflines reflect it: multiple gables, bonus rooms over garages, dormers and long valleys running into complicated wall junctions. Square footage of roof here is often half again what a Coon Rapids rambler carries, which changes both the cost and the failure points. The first wave of those homes went on with builder-grade 20- and 25-year shingles, and those are now failing on schedule. Siding is mostly vinyl with stone or brick accents on the front elevation, plus a lot of LP and engineered wood on the newer end of the city.",
    exteriorChallenges: [
      {
        title: "Builder-grade shingles reaching end of life together",
        body: "Whole Andover streets went up inside a two or three year window with the same shingle on every house. They wear out on the same schedule too, which is why one replacement on a block is usually followed by several more. If your neighbours are re-roofing and your home is the same vintage, an inspection is worth doing before a leak forces the timing.",
      },
      {
        title: "Complex rooflines that price and fail differently",
        body: "Valleys, dormers, steep pitches and sidewall transitions are where labour goes and where water gets in. On a cut-up Andover roof the flashing details matter more than the shingle line you pick. Ask any estimate you receive to itemise valley metal, step flashing and kick-out flashing rather than burying it in a lump sum, because that is the work that determines whether the roof lasts.",
      },
      {
        title: "Ventilation on bonus rooms and vaulted ceilings",
        body: "Rooms over garages and vaulted great rooms are chronically hard to ventilate, and they are where ice dams and premature shingle ageing show up first on newer homes. A vented ridge is not enough on its own if there is no clear intake path at the soffit. This gets missed on a lot of replacements because it is invisible from the driveway.",
      },
    ],
    faqs: [
      {
        question: "Do you offer free estimates in Andover?",
        answer:
          "Yes—free, no-obligation estimates for every Andover homeowner, including a clear written quote.",
      },
      {
        question: "How quickly can you respond to storm damage in Andover?",
        answer:
          "Andover is a core service area, so we respond quickly after storms—prioritizing your inspection and documenting any hail or wind damage.",
      },
      {
        question: "Do you help with insurance claims in Andover?",
        answer:
          "Yes. We document the damage, meet your adjuster, and make sure your claim covers everything your home needs.",
      },
      {
        question:
          "My Andover home is about 20 years old—could it need a new roof already?",
        answer:
          "Quite possibly. Many Andover homes were built with builder-grade shingles that wear out around the 20-year mark, especially after hail. We'll inspect and give you an honest timeline.",
      },
      {
        question: "Do complex Andover rooflines cost more to replace?",
        answer:
          "Larger homes with multiple valleys, dormers, and steep pitches take more labor and detail work, which affects price. We give you an itemized estimate so you know exactly what's included.",
      },
      {
        question: "What siding is popular on Andover homes?",
        answer:
          "LP SmartSide engineered wood is a favorite on Andover's newer homes for its durability and wood-like look; James Hardie fiber cement is the premium choice. We'll help you decide.",
      },
      {
        question: "What areas near Andover do you serve?",
        answer:
          "Andover plus Ham Lake, Anoka, Coon Rapids, Ramsey, Oak Grove, and the surrounding north metro.",
      },
    ],
  },
  {
    name: "Ham Lake",
    slug: "ham-lake",
    description:
      "Roofing, LP SmartSide siding, and seamless gutters for Ham Lake homes on acreage and in town.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Ham Lake sits between Blaine and East Bethel in Anoka County, and it has kept more of its rural character than the suburbs south of it. Larger lots, wooded acreage and custom-built homes are the norm rather than tract developments. Cedar Creek Construction has completed roofing and siding projects across Ham Lake, and it remains one of the areas we work in most often.",
      landmarks: [
        "Ham Lake",
        "Bunker Hills Regional Park",
        "Coon Lake Beach",
        "Ham Lake Town Center",
        "Constance area",
      ],
      neighborhoods: [
        "Ham Lake Town Center",
        "Constance",
        "Coon Lake Beach",
        "Crosstown Boulevard area",
        "East Ham Lake acreage",
      ],
    },
    intro:
      "Ham Lake is one of the places our crews are most often working, and it is genuinely different from the tighter suburbs to the south. Homes here sit on bigger lots, frequently custom-built rather than tract, and many are surrounded by mature oaks and pines. That changes what an exterior has to deal with: more shade and debris on the roof, more wind exposure on open acreage, and longer gutter runs on wide single-storey homes. We have completed roof replacement, LP siding and new-construction exterior work in Ham Lake.",
    housingStock:
      "There is no single Ham Lake house. You will find 1970s and 1980s ramblers on acreage, brick ranches on established lots, and newer custom builds with steep pitches and mixed material fronts. What they share is size and exposure: wide footprints, long roof planes and long gutter runs, often with detached garages, pole buildings or outbuildings that need the same roofing and siding attention as the house. Mature tree cover is close to universal, and a lot of homes carry LP SmartSide or engineered wood rather than the narrow vinyl common in the older suburbs.",
    exteriorChallenges: [
      {
        title: "Tree cover, debris and shaded roof planes",
        body: "Oaks and pines over the roof mean constant needle and leaf load in the gutters and north-facing planes that stay damp and grow moss and algae. Gutters on a treed Ham Lake lot fill faster than anywhere in the county, and clogged gutters in a Minnesota winter are how ice dams start. Guards sized for needles rather than leaves, and algae-resistant shingles on the shaded elevations, both earn their cost here.",
      },
      {
        title: "Wind exposure on open acreage",
        body: "A home sitting on open ground without a neighbouring house breaking the wind takes uplift that a home in a tight subdivision never sees. Ridge caps, rake edges and the first few courses along the eaves are where that shows up. Proper nailing patterns and starter strip at the perimeter matter more here than the shingle warranty on paper.",
      },
      {
        title: "Long gutter runs on wide single-storey homes",
        body: "Wide ramblers and ranches concentrate a lot of roof into a few long gutter runs. Undersized 5-inch gutter with too few downspouts overflows at the same corners every heavy rain and ices in the same spots every winter. Six-inch seamless with downspouts placed for the actual roof area is the fix, and on a wide home it is a meaningful upgrade rather than a cosmetic one.",
      },
      {
        title: "Outbuildings and detached garages",
        body: "Pole buildings, shops and detached garages are common on Ham Lake properties and they age faster than the house because nobody looks at them. Doing them at the same time as the main roof usually costs less per square than a separate trip later, and it keeps the property looking consistent.",
      },
    ],
    localProof:
      "Ham Lake is one of our most active areas. Published projects here include a brick ranch roof replacement, an LP SmartSide siding project with stone veneer, and an LP siding new build with a wood pergola.",
    faqs: [
      {
        question: "Do you offer free estimates in Ham Lake?",
        answer:
          "Yes. Free, no-obligation estimates for every Ham Lake homeowner, including detached garages, shops and pole buildings on the property. You get a clear written quote with the scope itemised.",
      },
      {
        question:
          "Do you work on acreage properties and outbuildings in Ham Lake?",
        answer:
          "We do, and it is common here. Detached garages, shops and pole buildings can usually be done alongside the house for less per square than scheduling them separately, and it keeps the whole property matching.",
      },
      {
        question:
          "My Ham Lake roof is shaded and growing moss. Is that a problem?",
        answer:
          "It is worth addressing. Heavy tree cover keeps north-facing planes damp, and moss and algae hold moisture against the shingles and shorten their life. On replacements in treed Ham Lake lots we normally recommend algae-resistant shingles and a gutter guard sized for needles rather than leaves.",
      },
      {
        question: "How quickly can you respond to storm damage in Ham Lake?",
        answer:
          "Ham Lake is one of the areas we work in most, so we prioritise inspections here after a storm. We document any hail or wind damage in full and get you on the schedule.",
      },
      {
        question: "Do you help Ham Lake homeowners with insurance claims?",
        answer:
          "Yes. We photograph and document the damage, meet your adjuster on site, and make sure the scope covers everything that was hit rather than shingles alone. Gutters, wraps, vents and ridge are routinely left off a first estimate.",
      },
      {
        question: "What siding works best on Ham Lake homes?",
        answer:
          "LP SmartSide engineered wood is the most common choice here and suits the custom and rural look well, especially paired with stone. James Hardie fibre cement is the premium option. Both handle Minnesota freeze-thaw far better than older narrow vinyl.",
      },
      {
        question: "What areas near Ham Lake do you serve?",
        answer:
          "Ham Lake plus Blaine, Andover, East Bethel, Coon Rapids, Anoka, Cedar and the rest of the north metro.",
      },
    ],
  },
  {
    name: "Cedar",
    slug: "cedar",
    description:
      "Your neighbors in Cedar trust us for their exterior projects.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Cedar is a small, tight-knit township in Anoka County where neighbors know each other by name. The rural character and open spaces make Cedar a special place to call home. We've been serving Cedar families for over 20 years, treating every home like it's our own.",
      landmarks: [
        "East Bethel Community Park",
        "Carlos Avery Wildlife Management Area",
        "Local family farms",
        "Cedar Creek",
        "Martin Lake",
      ],
      neighborhoods: [
        "Central Cedar",
        "Viking Boulevard area",
        "Lake Martin area",
      ],
    },
    housingStock:
      "Cedar is rural residential rather than suburban. Homes sit on acreage, often well back from the road down a long driveway, and are frequently surrounded by heavy oak and pine cover. The mix runs from 1970s and 1980s ramblers to newer custom builds, with a strong showing of detached garages, shops and pole buildings. Wide single-storey footprints are common, which means long roof planes and long gutter runs. The tree cover is the single biggest factor in how exteriors age here.",
    exteriorChallenges: [
      {
        title: "Heavy tree cover, debris and shaded roof planes",
        body: "Being next to Carlos Avery and surrounded by mature trees means constant needle and leaf load. Gutters fill faster here than almost anywhere in the county, and north-facing planes stay damp enough to grow moss and algae. Gutter guards sized for needles rather than leaves, and algae-resistant shingles on shaded elevations, both earn their cost on a Cedar property.",
      },
      {
        title: "Wildlife getting into soffits and roof edges",
        body: "Rural wooded properties get squirrels, birds and other wildlife working at damaged soffit, fascia and roof edges. Once something is in the attic the damage compounds quickly. Repairing the entry point properly, rather than blocking it and leaving the rot, is what stops it recurring.",
      },
      {
        title: "Access for equipment down long driveways",
        body: "A long, narrow or soft driveway affects where a dumpster and material delivery can go, and a contractor who does not plan for it either damages the drive or adds cost mid-job. It is worth settling before the work starts, and it is a normal part of how we quote rural properties.",
      },
      {
        title: "Long gutter runs on wide homes",
        body: "Wide ramblers concentrate a lot of roof into a few long runs. Five-inch gutter with too few downspouts overflows in heavy rain and ices in winter at the same corners every year. Six-inch seamless with downspouts placed for the real roof area is a genuine upgrade, not a cosmetic one.",
      },
    ],
    intro:
      "Cedar sits in the rural stretch of northern Anoka County near the Carlos Avery Wildlife Management Area, and it is close to our St Francis base. Properties here tend to be wooded, set back from the road and larger than anything in the suburbs, which changes what an exterior has to put up with.",
    faqs: [
      {
        question: "Do you serve Cedar and the rural properties around it?",
        answer:
          "Yes. Cedar is close to our St Francis base and a normal part of our service area, including properties on acreage with long driveways and multiple outbuildings.",
      },
      {
        question:
          "My roof is under heavy tree cover and growing moss. What should I do?",
        answer:
          "It is worth addressing rather than ignoring. Moss and algae hold moisture against the shingles and shorten their life. On a replacement we would normally recommend algae-resistant shingles on the shaded elevations and a gutter guard designed for pine needles rather than leaves.",
      },
      {
        question:
          "My gutters fill up constantly. Are guards actually worth it here?",
        answer:
          "On a wooded Cedar property, generally yes, provided they are the right type. Guards designed for leaves let pine needles straight through. We size the guard to what is actually falling on your roof.",
      },
      {
        question: "Can you get equipment down my driveway?",
        answer:
          "We plan that before the job starts. Long, narrow or soft driveways affect where the dumpster and material delivery go, and we sort it out up front rather than damaging the drive or adding cost partway through.",
      },
      {
        question: "Can you do the pole building and detached garage too?",
        answer:
          "Yes, and doing them alongside the house costs less per square than a separate trip later. On acreage properties it is usually the efficient approach.",
      },
      {
        question: "Do you help with insurance claims in Cedar?",
        answer:
          "Yes. We document the damage in full, meet your adjuster on site, and make sure the scope reflects everything that was hit rather than shingles alone.",
      },
      {
        question: "What areas near Cedar do you serve?",
        answer:
          "Cedar plus East Bethel, Bethel, St Francis, Ham Lake, Isanti, Andover and the surrounding north metro.",
      },
    ],
  },
  {
    name: "Saint Francis",
    slug: "saint-francis",
    description: "Reliable roofing and gutter services in Saint Francis.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Saint Francis combines small-town values with convenient access to the Twin Cities. Known for its excellent school district and community spirit, Saint Francis has attracted families looking for a close-knit community. We've proudly served Saint Francis for over 20 years.",
      landmarks: [
        "St. Francis Regional Park",
        "Rum River Central Regional Park",
        "Springbrook Nature Center",
        "Historic Main Street",
        "Community fields",
      ],
      neighborhoods: [
        "Downtown St. Francis",
        "West St. Francis",
        "Rum River area",
        "Ambassador Boulevard area",
      ],
    },
    housingStock:
      "St Francis is our home city, and it is a small one: a compact centre along the Rum River with rural land and acreage in every direction. The housing runs from older in-town homes on small lots to 1990s and 2000s subdivision builds on the edges, out to farmsteads and acreage properties with detached garages, shops and pole buildings. In town the roofs are mostly simple gables with straightforward details. Outside it, you get wide single-storey footprints, long gutter runs, heavy tree cover on some parcels and complete wind exposure on others.",
    exteriorChallenges: [
      {
        title: "Being local actually matters here",
        body: "We are based in St Francis, so this is the one place where a small repair, a second look at something after a storm, or a quick question does not need to be scheduled around a drive. That changes what is practical: problems get looked at while they are still small and cheap.",
      },
      {
        title: "Subdivision roofs reaching end of life together",
        body: "The homes built on the edges of town through the 1990s and 2000s went on with builder-grade shingles that are now at or past their rated life. They tend to fail across a street rather than one at a time, so if your neighbours are re-roofing, yours is likely the same vintage and specification.",
      },
      {
        title: "Acreage properties with several roofs",
        body: "Outside the centre, most properties have a house plus a detached garage and often a shop or pole building. The outbuildings age faster because nobody looks at them, and handling them on the same mobilisation is meaningfully cheaper than separate visits.",
      },
      {
        title: "Snow that sits, and eaves that ice",
        body: "This far north the snow stays longer and the freeze-thaw is harder on the roof edge. Ice and water shield well past the interior wall line, plus a real intake-to-ridge ventilation path, are what prevent ice dams rather than just repairing the damage they cause.",
      },
    ],
    localProof:
      "St Francis is our home base and we have published project work here. We work outward from St Francis across Bethel, East Bethel, Cedar, Ham Lake, Isanti, Andover and the wider north metro.",
    faqs: [
      {
        question: "Are you actually based in St Francis?",
        answer:
          "Yes. St Francis is our home city, which is why we can look at a small problem here without it needing to become a scheduled project. It is also why we cover the northern communities that south metro contractors treat as a long drive.",
      },
      {
        question: "Do you offer free estimates in St Francis?",
        answer:
          "Yes. Free, no-obligation estimates with a clear written quote, and outbuildings can be included in the same estimate.",
      },
      {
        question: "Will you come out for a small repair?",
        answer:
          "Yes, and being local makes that practical. A failed vent boot or a lifted section of shingles costs very little to fix now and a great deal more after two winters of water getting into the sheathing.",
      },
      {
        question: "My St Francis home is 20 to 25 years old. Is it time?",
        answer:
          "It may well be. Many of the subdivision homes built here in the 1990s and 2000s used 20 and 25 year shingles and are now at that mark. We will inspect and tell you honestly whether you have years left or should start planning.",
      },
      {
        question: "Can you handle the pole building and detached garage too?",
        answer:
          "Yes, and on an acreage property it is usually the efficient way to do it. The same mobilisation covers all of it at a lower cost per square than separate trips.",
      },
      {
        question: "Do you help with insurance claims in St Francis?",
        answer:
          "Yes. We document hail and wind damage in full, meet your adjuster on site, and make sure the scope covers the whole exterior rather than shingles alone.",
      },
      {
        question: "What areas around St Francis do you serve?",
        answer:
          "St Francis plus Bethel, East Bethel, Cedar, Ham Lake, Isanti, Cambridge, Andover, Anoka, Blaine and the rest of the north metro.",
      },
    ],
  },
  {
    name: "Bethel",
    slug: "bethel",
    description: "Serving the Bethel community with expert craftsmanship.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Bethel is a peaceful rural community in Anoka County where residents enjoy wide open spaces and a slower pace of life. The area features beautiful wooded properties and a strong sense of community. We've been honored to serve Bethel homeowners for over 20 years.",
      landmarks: [
        "Bethel Heritage Park",
        "Wild River State Park (nearby)",
        "Local farms and orchards",
        "Sunrise River",
        "Historic Bethel Church",
      ],
      neighborhoods: [
        "Central Bethel",
        "Highway 65 corridor",
        "Sunrise River area",
      ],
    },
    housingStock:
      "Bethel is a mix of modest older homes near the centre of town and rural properties on acreage around it. In town you will find mid-century and older builds on small lots with straightforward gable roofs, often with original siding and gutters that have been repaired rather than replaced. Outside the centre the pattern shifts to farmhouses, manufactured and modular homes, and properties with a detached garage, shop or pole building alongside the house. Tree cover varies sharply from one property to the next, and so does wind exposure.",
    exteriorChallenges: [
      {
        title: "Deferred maintenance because nobody comes out",
        body: "The practical reality of a small town this far north is that fewer contractors bother with it, so small problems sit longer than they should. A failed boot around a plumbing vent or a section of lifted shingles is cheap to fix and expensive to ignore for three years. Being close by means we can look at something small without it needing to be a full project.",
      },
      {
        title: "Multiple buildings on one property",
        body: "Rural Bethel properties usually have more than one roof. The house, a detached garage and often a shop or pole building all age at different rates and all get overlooked. Doing them together costs less per square than separate visits and keeps the property consistent.",
      },
      {
        title: "Wind exposure on open ground",
        body: "A home on open rural land takes uplift a subdivision home never sees. That shows up at ridge caps, rake edges and the eave courses. Correct perimeter nailing and starter strip matter more here than the shingle line on the quote.",
      },
      {
        title: "Farmhouse additions and mismatched rooflines",
        body: "Older rural homes often grew in stages, and the junctions where a later addition meets the original roof are where water gets in. Those transitions need proper flashing rather than sealant, and they are worth having looked at specifically.",
      },
    ],
    intro:
      "Bethel is a small city in northern Anoka County, and small is the operative word: a compact centre surrounded by rural land along the Highway 65 corridor. It is close to our home base in St Francis, so it is an easy area for us to reach, which is not true of most contractors working out of the south metro.",
    faqs: [
      {
        question: "Do you serve Bethel, or is it too small and too far north?",
        answer:
          "We serve Bethel regularly. Our home base is in St Francis, just down the road, so Bethel is one of the closest areas we work in rather than a long haul.",
      },
      {
        question: "Do you offer free estimates in Bethel?",
        answer:
          "Yes. Free, no-obligation estimates with a clear written quote, and we can include detached garages, shops and pole buildings in the same estimate.",
      },
      {
        question:
          "Will you come out for a small repair, not just a full replacement?",
        answer:
          "Yes. Being close by means a small repair is practical for us. A failed vent boot or a lifted section of shingles is far cheaper to address early than after it has been leaking into the sheathing for a couple of winters.",
      },
      {
        question: "Can you do my pole building or shop as well as the house?",
        answer:
          "Yes, and it is usually the sensible way to do it. Outbuildings on the same mobilisation cost less per square than bringing a crew back separately.",
      },
      {
        question: "Do you help with insurance claims in Bethel?",
        answer:
          "Yes. We document hail and wind damage thoroughly, meet your adjuster on site, and make sure the scope covers the whole exterior rather than shingles alone.",
      },
      {
        question: "What siding holds up on a rural Bethel property?",
        answer:
          "LP SmartSide engineered wood is the common choice and handles Minnesota freeze-thaw well, including in board and batten profiles that suit a rural look. James Hardie fibre cement is the premium option.",
      },
      {
        question: "What areas near Bethel do you serve?",
        answer:
          "Bethel plus St Francis, East Bethel, Cedar, Isanti, Cambridge, Ham Lake and the rest of the north metro and northern counties.",
      },
    ],
  },
  {
    name: "Isanti",
    slug: "isanti",
    description: "Professional exterior solutions for Isanti homeowners.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Isanti is a growing city that maintains its small-town charm while offering modern amenities. The community is known for its family-oriented atmosphere and beautiful natural surroundings. We've been serving Isanti residents with quality craftsmanship for over 20 years.",
      landmarks: [
        "Bluebird Park",
        "Isanti County Fairgrounds",
        "Rum River",
        "Downtown Isanti shops",
        "Spectacle Lake",
      ],
      neighborhoods: [
        "Downtown Isanti",
        "Blue Sky area",
        "Rum River corridor",
        "West Isanti",
      ],
    },
    housingStock:
      "In town, Isanti runs to modest older homes and mid-century builds on smaller lots, generally simple gable roofs with original or first-replacement siding and undersized gutters. Around the edges, subdivision development through the late 1990s and 2000s added a wave of conventional two-storeys and split-levels, nearly all finished with builder-grade shingles and vinyl. Those are now twenty to twenty-five years old, which is exactly when that specification fails. Beyond the city you are into acreage and farmstead properties with outbuildings and open exposure.",
    exteriorChallenges: [
      {
        title: "A whole generation of subdivision roofs failing at once",
        body: "The homes built here through the late 1990s and 2000s went on with 20 and 25 year shingles, and they are reaching that mark together. If neighbours on your street are re-roofing, your home is likely the same vintage with the same specification. An inspection before a leak forces the timing is worth doing.",
      },
      {
        title: "Rum River corridor and open-field wind",
        body: "Properties along the river corridor and on open ground outside town take more wind than a sheltered subdivision lot. Uplift shows first at ridge caps, rake edges and the first courses along the eaves, so perimeter fastening and starter strip matter here.",
      },
      {
        title: "Snow that sits longer than it does in the metro",
        body: "Isanti County holds snow longer than the south metro, which makes eave protection and attic ventilation more important. Ice and water shield well past the interior wall line and a genuinely working intake-to-ridge ventilation path are the things that prevent ice damming rather than just repairing it.",
      },
      {
        title: "Outbuildings and detached garages",
        body: "Acreage properties around Isanti usually have more than one roof. Pole buildings, shops and detached garages age faster than the house because nobody inspects them, and they cost less per square to do alongside the main roof than on a separate visit.",
      },
    ],
    localProof:
      "We have completed a published roof replacement project in Isanti, and we work throughout Isanti County including Cambridge, Braham and Princeton.",
    intro:
      "Isanti is a small city with a growing edge, sitting on the Rum River in Isanti County. We work here often, and the jobs split cleanly between older homes near the centre of town and the newer subdivisions that went up through the 2000s, which are now reaching the age where the original roof gives out.",
    faqs: [
      {
        question: "Do you offer free estimates in Isanti?",
        answer:
          "Yes. Free, no-obligation estimates for every Isanti homeowner, with the scope itemised in a clear written quote. Outbuildings can be included.",
      },
      {
        question:
          "My Isanti home is about 20 years old. Could it already need a roof?",
        answer:
          "Quite possibly. A lot of the subdivision homes built here through the late 1990s and 2000s went on with 20 and 25 year shingles, and they are reaching that mark together. If neighbours on your street are re-roofing, an inspection is worth doing before a leak decides the timing for you.",
      },
      {
        question: "Do you actually travel to Isanti regularly?",
        answer:
          "Yes. Isanti County is a normal part of our service area rather than an occasional long drive, which matters most when you need a storm inspection or a leak looked at quickly in winter.",
      },
      {
        question: "Does the snow up here change what you recommend?",
        answer:
          "It changes the details. Isanti County holds snow longer than the south metro, so ice and water shield coverage at the eaves and valleys and a genuinely working attic ventilation path matter more here than they do thirty miles south.",
      },
      {
        question: "Can you do a pole building or detached garage as well?",
        answer:
          "Yes. On acreage properties around Isanti it is usually the sensible approach, since doing them alongside the main roof costs less per square than a separate visit.",
      },
      {
        question: "Do you help with insurance claims in Isanti?",
        answer:
          "Yes. We document the damage thoroughly, meet your adjuster on site, and make sure the claim reflects everything that was hit rather than shingles alone.",
      },
      {
        question: "What areas near Isanti do you serve?",
        answer:
          "Isanti plus Cambridge, Braham, Princeton, East Bethel, Bethel, St Francis and the rest of the northern counties and north metro.",
      },
    ],
  },
  {
    name: "Cambridge",
    slug: "cambridge",
    description: "Extending our quality services to Cambridge and beyond.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Cambridge is the county seat of Isanti County and serves as a regional hub for shopping, dining, and services. The city offers a wonderful mix of historic character and modern development. We've extended our services to Cambridge and have proudly served the community for over 20 years.",
      landmarks: [
        "Cambridge State Hospital Historic District",
        "Isanti County Historical Society",
        "Downtown Cambridge",
        "Rum River",
        "Cambridge Medical Center",
      ],
      neighborhoods: [
        "Downtown Cambridge",
        "North Cambridge",
        "South Cambridge",
        "Highway 95 corridor",
      ],
    },
    housingStock:
      "Cambridge splits three ways. The older core near downtown and the historic district has homes from the early 1900s through mid-century, generally steeper roofs, wood trim and additions built across several decades that leave awkward roofline junctions. The subdivisions that went up from the 1990s through the 2000s on the edges of town are conventional suburban builds with builder-grade shingles now reaching end of life. Outside the city limits you are into farmsteads and acreage properties with the house, a detached garage and often a pole building or barn, all needing attention and all exposed to open-field wind.",
    exteriorChallenges: [
      {
        title: "Heavier snow load than the metro",
        body: "Cambridge sits far enough north that snow accumulation is consistently heavier than in the south metro, and it sits longer. That puts more load on the roof structure and makes proper ice and water protection at the eaves and valleys more important, not less. Cutting that detail to save money this far north is a false economy.",
      },
      {
        title: "Additions that meet the original roof badly",
        body: "Older Cambridge homes commonly have one or two additions built decades apart. Where a newer roof plane ties into the original, you get valleys, dead-end junctions and sometimes a low-slope connector that was never detailed properly. These transitions are the leak points on a lot of the homes here.",
      },
      {
        title: "Open-field wind exposure",
        body: "A farmstead or an edge-of-town lot with nothing upwind takes uplift that a home mid-subdivision never sees. Perimeter nailing, starter strip at the eaves and rakes, and properly fastened ridge cap do more for a roof out here than an upgraded shingle line does.",
      },
      {
        title: "Getting a metro contractor to actually show up",
        body: "The practical problem in Cambridge is often not price but attention. Contractors based in the south metro treat it as a long drive and schedule it last. We serve the northern counties as a matter of course, which is worth something when you are trying to get a leak looked at in February.",
      },
    ],
    intro:
      "Cambridge sits north of the metro in Isanti County, far enough out that plenty of Twin Cities contractors treat it as an inconvenient drive. We work here regularly. The mix of older in-town homes, newer subdivisions on the edges and farmsteads on acreage means there is no single Cambridge job, and the weather this far north is genuinely harder on an exterior than it is thirty miles south.",
    faqs: [
      {
        question:
          "Do you actually travel to Cambridge, or is it an afterthought?",
        answer:
          "We work in Cambridge and across Isanti County regularly. It is a normal part of our service area rather than an occasional long drive, which matters most when you need a storm inspection or a leak looked at quickly.",
      },
      {
        question: "Do you offer free estimates in Cambridge?",
        answer:
          "Yes. Free, no-obligation estimates for every Cambridge homeowner, with a clear written quote that itemises the scope. Outbuildings and detached garages can be included in the same estimate.",
      },
      {
        question: "Does the heavier snow up here change how you build a roof?",
        answer:
          "It changes the details we insist on. Ice and water shield coverage at the eaves and valleys matters more this far north, as does correct attic ventilation, because snow sits longer and the freeze-thaw cycles are harder on the roof edge.",
      },
      {
        question: "Can you do the barn, pole building or detached garage too?",
        answer:
          "Yes, and on acreage properties it is usually the efficient way to do it. Handling the outbuildings on the same mobilisation costs less per square than scheduling them as a separate trip later.",
      },
      {
        question:
          "My Cambridge home has an addition and it leaks where it meets the old roof. Can that be fixed properly?",
        answer:
          "Usually yes. Those junctions leak because the original transition was never flashed correctly or a low-slope connector was shingled. We look at how the planes actually meet and detail the transition properly rather than sealing over it.",
      },
      {
        question: "Do you help with insurance claims in Cambridge?",
        answer:
          "Yes. We document hail and wind damage in full, meet your adjuster on site, and make sure the scope covers everything that was hit rather than shingles alone.",
      },
      {
        question: "What areas near Cambridge do you serve?",
        answer:
          "Cambridge plus Isanti, Braham, Princeton, North Branch, East Bethel, Bethel and the rest of the northern counties, along with the north metro.",
      },
    ],
  },
  {
    name: "Coon Rapids",
    slug: "coon-rapids",
    description:
      "Trusted roofing and siding services for Coon Rapids homeowners.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Coon Rapids is one of the largest cities in the Twin Cities metro area, known for its beautiful parks along the Mississippi River and excellent community amenities. We've been serving Coon Rapids homeowners with quality exterior services for over 20 years.",
      landmarks: [
        "Coon Rapids Dam Regional Park",
        "Bunker Hills Regional Park",
        "Sand Creek Park",
        "Mississippi River Trail",
        "Coon Rapids Civic Center",
      ],
      neighborhoods: [
        "Riverview",
        "Crooked Lake",
        "Bunker Hills",
        "Hanson Boulevard area",
        "Foley Boulevard area",
      ],
    },
    intro:
      "Coon Rapids is one of the largest north-metro suburbs, with a big stock of 1970s–1990s homes—many now on their second or third roof and original siding. We replace a lot of aging asphalt roofs and tired vinyl here, and like the rest of Anoka County, Coon Rapids takes its share of summer hail and heavy winter snow. We know the neighborhoods from Riverview to Crooked Lake and what their homes need.",
    housingStock:
      "Coon Rapids is an older suburb than its neighbours, and it shows on the exteriors. The bulk of the city was built out between the 1960s and the 1980s, which means most homes are now on their second or third roof and a good number still wear their original siding. The dominant shapes are ramblers, split-entries and split-levels: simpler roofs than Andover or Blaine, but with low-slope sections over porches, entries and tuck-under garages that asphalt shingles were never meant to cover. Those low-slope areas are where we find most of the active leaks. Siding runs to original narrow-profile vinyl or aluminium, much of it faded, chalking or brittle enough to crack when a panel is pulled.",
    exteriorChallenges: [
      {
        title: "Low-slope porch and garage roofs shingled over",
        body: "A rambler or split-entry with a shallow entry roof or a tuck-under garage almost always has a section under the minimum pitch for shingles. Shingled anyway, those sections back up water under the tabs and leak long before the main roof fails. They need a membrane, not another layer of asphalt, and that is a distinction worth insisting on in any estimate you get.",
      },
      {
        title: "Ice dams on under-insulated 1970s attics",
        body: "Homes of this era were routinely built with thin attic insulation, no baffles at the eaves and bath fans dumping into the attic instead of outside. That combination melts snow from below and refreezes it at the cold overhang. Tearing off and re-shingling does nothing about it. Ice and water shield, proper intake and exhaust ventilation, air sealing and topped-up insulation are what actually stop it coming back.",
      },
      {
        title: "Original siding that is past repair",
        body: "Narrow 1970s and 1980s vinyl is usually out of production, so colour-matched repairs are rarely possible and patches show. Aluminium of the same age dents and chalks. Once a wall has enough damage, full replacement in LP SmartSide or fibre cement costs less than chasing panels and looks like a different house.",
      },
      {
        title: "Second and third layers of shingles",
        body: "Older Coon Rapids roofs frequently carry a layover from a previous replacement. Minnesota code caps you at two layers, so a third means a full tear-off, and the added weight and trapped heat shorten the life of whatever goes on top. We check layer count during the inspection because it changes both the price and the plan.",
      },
    ],
    faqs: [
      {
        question: "Do you offer free estimates in Coon Rapids?",
        answer:
          "Yes—free, no-obligation estimates for every Coon Rapids homeowner. We inspect your roof, siding, or gutters, document any issues, and give you a clear written quote.",
      },
      {
        question: "How quickly can you respond to storm damage in Coon Rapids?",
        answer:
          "Coon Rapids is one of our core service areas, so we respond quickly after storms. We'll prioritize your inspection, document any hail or wind damage, and get you on the schedule.",
      },
      {
        question: "Do you help with insurance claims in Coon Rapids?",
        answer:
          "Yes. Many Coon Rapids roofs we replace are storm claims. We document the damage, meet your adjuster, and make sure your claim reflects the full scope of work.",
      },
      {
        question:
          "My Coon Rapids home still has its original roof and siding—is it time to replace?",
        answer:
          "Many Coon Rapids homes from the 70s–90s are past the typical 20–25 year life of asphalt shingles and original vinyl siding. We'll inspect honestly and tell you whether you have years left or it's time to plan a replacement.",
      },
      {
        question: "Are ice dams common on Coon Rapids homes?",
        answer:
          "Yes, especially on older homes with limited attic insulation and ventilation. We repair ice-dam damage and correct the underlying causes to help prevent it.",
      },
      {
        question: "What siding do you recommend for Coon Rapids homes?",
        answer:
          "A lot of Coon Rapids homes are ready to upgrade from original vinyl. We install LP SmartSide and James Hardie for durability and curb appeal, plus modern insulated vinyl when budget is the priority.",
      },
      {
        question: "What areas near Coon Rapids do you serve?",
        answer:
          "Coon Rapids plus Blaine, Anoka, Andover, Fridley, Champlin, and the surrounding north metro.",
      },
    ],
  },
  {
    name: "East Bethel",
    slug: "east-bethel",
    description: "Reliable exterior services for East Bethel residents.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "East Bethel is a growing city in Anoka County known for its rural character and natural beauty. With extensive wetlands and wildlife areas, East Bethel offers a peaceful setting for families. We've proudly served East Bethel homeowners for over 20 years.",
      landmarks: [
        "East Bethel Community Center",
        "Carlos Avery Wildlife Management Area",
        "Coon Lake",
        "Viking Boulevard corridor",
        "Boot Lake Scientific and Natural Area",
      ],
      neighborhoods: [
        "Viking Boulevard area",
        "Coon Lake area",
        "Central East Bethel",
        "Highway 65 corridor",
      ],
    },
    housingStock:
      "East Bethel is rural and exurban rather than suburban. Homes sit on acreage, often wooded, often with a detached garage, shop or pole building alongside. The mix spans 1970s and 1980s ramblers, lake-area properties around Coon Lake, and newer custom builds with steeper pitches and mixed material fronts. Wide single-storey footprints are the norm, which means long roof planes, long gutter runs and more exposure to wind on open parcels. LP SmartSide and engineered wood are common here, including board and batten profiles that suit the rural look better than narrow vinyl.",
    exteriorChallenges: [
      {
        title: "Open-parcel wind exposure",
        body: "A home on open acreage with nothing upwind takes uplift a subdivision home is sheltered from. Ridge caps, rake edges and the first courses at the eaves are where it shows. Perimeter nailing patterns and starter strip do more for durability out here than paying up a shingle line.",
      },
      {
        title: "Tree debris and shaded planes near the lakes and Carlos Avery",
        body: "Wooded and lake-adjacent properties collect needles and leaves constantly, and north-facing planes stay damp. That means gutters that clog and roof surfaces that grow moss. Needle-rated gutter guards and algae-resistant shingles are the practical answers.",
      },
      {
        title: "Multiple buildings that age at different rates",
        body: "Most East Bethel properties have more than one roof. Shops, pole buildings and detached garages get inspected far less often than the house and are usually further gone. Handling them on the same mobilisation is materially cheaper than a separate visit.",
      },
      {
        title: "Long gutter runs on wide homes",
        body: "Wide ramblers push a lot of roof area into a few long gutter runs. Undersized gutter with too few downspouts overflows at the same corners in every heavy rain and ices in the same spots each winter. Six-inch seamless sized to the roof is the fix.",
      },
    ],
    localProof:
      "Published East Bethel work includes an LP SmartSide board and batten siding project. We work across the surrounding area in Cedar, Bethel, St Francis, Ham Lake and Isanti.",
    intro:
      "East Bethel covers a lot of ground. It is one of the larger cities by area in Anoka County and one of the least dense, with acreage properties, lakes and stretches of wildlife land rather than subdivisions. It is close to our St Francis base and one of the areas we work in most consistently.",
    faqs: [
      {
        question:
          "Do you serve all of East Bethel, including the outlying acreage?",
        answer:
          "Yes. East Bethel is large and spread out, and we work across all of it. Our St Francis base is close by, so the outlying parcels are not a problem the way they are for contractors coming up from the south metro.",
      },
      {
        question: "Do you offer free estimates in East Bethel?",
        answer:
          "Yes. Free, no-obligation estimates with a clear written quote, and we can include detached garages, shops and pole buildings in the same visit.",
      },
      {
        question:
          "Can you do my shop or pole building at the same time as the house?",
        answer:
          "Yes, and it is usually the efficient choice. Outbuildings handled on the same mobilisation cost less per square than a separate trip, and on most East Bethel properties they are further gone than the house because nobody inspects them.",
      },
      {
        question:
          "My property is wooded and the gutters clog constantly. What helps?",
        answer:
          "A guard rated for pine needles rather than leaves makes the real difference on a wooded lot. Standard leaf guards let needles straight through. We size the guard to what is actually landing on your roof.",
      },
      {
        question: "Is wind a real problem on open acreage here?",
        answer:
          "It is. A home with nothing upwind takes uplift that a sheltered subdivision home never sees, and it shows up at the ridge, the rake edges and the first courses at the eaves. Correct perimeter nailing and starter strip matter more here than paying up a shingle line.",
      },
      {
        question: "Do you help East Bethel homeowners with insurance claims?",
        answer:
          "Yes. We document hail and wind damage in full, meet your adjuster on site, and make sure the scope covers the whole exterior rather than shingles alone. Gutters, wraps, vents and ridge are routinely left off a first estimate.",
      },
      {
        question: "What areas near East Bethel do you serve?",
        answer:
          "East Bethel plus Cedar, Bethel, St Francis, Ham Lake, Isanti, Andover, Blaine and the rest of the north metro.",
      },
    ],
  },
  {
    name: "Brooklyn Park",
    slug: "brooklyn-park",
    description:
      "Expert roofing and exterior services for Brooklyn Park homeowners.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Brooklyn Park is one of the largest cities in Minnesota and a thriving suburban community in Hennepin County. Known for its diverse population, excellent parks, and family-friendly atmosphere, Brooklyn Park offers a wonderful mix of residential neighborhoods. We've been proudly serving Brooklyn Park homeowners for over 20 years.",
      landmarks: [
        "Edinburgh USA Golf Course",
        "Brooklyn Park Sports Dome",
        "Rush Creek Regional Trail",
        "Zanewood Recreation Center",
        "Central Park",
      ],
      neighborhoods: [
        "Edinburgh",
        "Hartkopf",
        "Willowstone",
        "Palmer Lake",
        "Lakeland",
      ],
    },
    intro:
      "Brooklyn Park is one of Minnesota's largest cities, with a wide range of housing—from established 1970s–90s developments to newer neighborhoods around Edinburgh. We replace plenty of aging roofs and siding across the city, and Brooklyn Park gets the same hail, wind, and heavy snow as the rest of the metro. We've proudly served Brooklyn Park homeowners for over 20 years.",
    housingStock:
      "Brooklyn Park is one of the largest cities in the state and its housing spans roughly fifty years, which means the right answer for a home here depends heavily on which part of the city it is in. South and central Brooklyn Park is largely 1960s through 1980s: ramblers, splits and modest two-storeys now on their second or third roof, with a lot of original or first-replacement siding. North of Highway 610 and out toward Edinburgh the builds get newer and larger, with steeper pitches, cut-up rooflines and vinyl with stone or brick accent fronts. The city also has a meaningful amount of townhome, multi-family and small commercial property, which is different work again from a single-family tear-off.",
    exteriorChallenges: [
      {
        title: "Two very different halves of the city",
        body: "An estimate that makes sense for a 1970s rambler off Zane Avenue is the wrong estimate for a 2005 two-storey near Edinburgh. The older stock is dealing with end-of-life siding and attic ventilation, the newer stock with builder-grade shingles failing on schedule and complex flashing. We inspect for the problems that home's era actually has rather than running a single template.",
      },
      {
        title: "Cedar shake and shake-look conversions",
        body: "A number of Brooklyn Park homes went up with cedar shake or shake-profile siding that has since weathered badly. Replacing it well means matching the texture and shadow line that gave the house its character, which LP SmartSide shake panels do convincingly. A flat lap panel on a home designed for shake changes its look more than homeowners expect.",
      },
      {
        title: "Association and covenant colour rules",
        body: "Newer Brooklyn Park developments frequently have an association with approved colour and material lists. Getting siding or a roof colour approved before the order goes in avoids a genuinely expensive problem. We flag this early when a home is in a development likely to have covenants.",
      },
      {
        title: "Townhome and commercial work is its own job",
        body: "Multi-unit and commercial exteriors involve shared walls, staged access, tenant or resident notice and often a board rather than a single homeowner making the decision. It is a different process from a single-family replacement, and worth engaging a contractor who has actually done it.",
      },
    ],
    localProof:
      "Published Brooklyn Park projects include a two-storey roof replacement and a commercial strip mall siding project.",
    faqs: [
      {
        question: "Do you offer free estimates in Brooklyn Park?",
        answer:
          "Yes—free, no-obligation estimates for every Brooklyn Park homeowner, with a clear written quote and no pressure.",
      },
      {
        question:
          "How quickly can you respond to storm or hail damage in Brooklyn Park?",
        answer:
          "We serve Brooklyn Park regularly and respond quickly after storms—prioritizing inspections and documenting hail or wind damage for your claim.",
      },
      {
        question: "Do you help Brooklyn Park homeowners with insurance claims?",
        answer:
          "Yes. We document the damage, meet your adjuster on site, and make sure the scope reflects what your home actually needs.",
      },
      {
        question: "Are ice dams a problem on Brooklyn Park homes?",
        answer:
          "They can be, particularly on older homes with limited attic insulation and ventilation. We repair the damage and fix the underlying causes.",
      },
      {
        question: "What siding do you recommend for Brooklyn Park homes?",
        answer:
          "We install LP SmartSide and James Hardie for durability and curb appeal, plus modern insulated vinyl as a budget-friendly option—matched to your home and neighborhood.",
      },
      {
        question: "How long does a roof replacement take in Brooklyn Park?",
        answer:
          "Most homes are done in a single day; larger or more complex roofs may take two. We always finish with a full cleanup and magnet sweep.",
      },
      {
        question: "What areas near Brooklyn Park do you serve?",
        answer:
          "Brooklyn Park plus Brooklyn Center, Maple Grove, Champlin, Osseo, Coon Rapids, and the surrounding area.",
      },
    ],
  },
  {
    name: "Blaine",
    slug: "blaine",
    description:
      "Quality roofing, siding, and gutter services for Blaine residents.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Blaine is a fast-growing city in Anoka County, known for the National Sports Center and its excellent schools. With beautiful lakes and well-planned neighborhoods, Blaine has become one of the most desirable suburbs in the Twin Cities. We've been serving Blaine homeowners with quality craftsmanship for over 20 years.",
      landmarks: [
        "National Sports Center",
        "Blaine Wetland Sanctuary",
        "Radisson Blu Mall of America Area",
        "Aquatore",
        "Lakes Community Park",
      ],
      neighborhoods: [
        "Lakes",
        "Sunrise",
        "Lexington",
        "Central Blaine",
        "Pheasant Ridge",
      ],
    },
    intro:
      "Blaine sits squarely in Anoka County's hail belt, and homeowners here know it—major summer storms have sent our crews across the city replacing roofs, siding, and gutters, and we've handled countless storm claims here since. Blaine's mix of established neighborhoods like Lexington and newer developments near the National Sports Center means we work on everything from 1990s asphalt roofs due for replacement to new-construction exteriors. Whatever your home's age, we know how Blaine's weather and building codes affect it.",
    housingStock:
      "Blaine grew fastest from the mid-1990s through the 2000s, so a large share of the city is now hitting the exact window where builder-grade asphalt gives out. Roofs installed with 20- and 25-year shingles when those neighbourhoods went up are due or overdue, and many are on their first replacement rather than their third. East of Highway 65 and around the National Sports Center you get newer two-storey homes with steeper pitches, multiple valleys and front-facing gables that take more flashing detail and more labour than a simple ranch. Older pockets closer to Lexington and Central Blaine run smaller and lower-slope, often with original aluminium or early vinyl siding and undersized 5-inch gutters that were never matched to the roof area feeding them.",
    exteriorChallenges: [
      {
        title: "Hail claims that get underscoped",
        body: "Anoka County takes real hail, and Blaine sits in the middle of it. The recurring problem is not getting a claim approved, it is getting the scope right. Adjusters routinely write for shingles alone and leave out the ridge, the vents, the gutters and the aluminium wrap that took the same hits. We inspect and document all of it before the adjuster meeting so the scope reflects the whole exterior.",
      },
      {
        title: "Steep, cut-up rooflines on the newer builds",
        body: "The two-storey homes built through the 2000s carry a lot of geometry: valleys, dormers, sidewall transitions and chimney saddles. Those are the spots that leak first, and they are also the spots a fast crew skips. Proper valley metal, step flashing and kick-out flashing at every wall-roof junction matter more here than the shingle brand on the estimate.",
      },
      {
        title: "Gutters sized for the wrong roof",
        body: "Plenty of Blaine homes have big roof planes draining into 5-inch gutters with too few downspouts. The result is overflow at the valleys in a hard rain and ice build-up at the same corners every winter. Moving to 6-inch seamless with correctly placed downspouts usually solves a problem homeowners assumed was a roof issue.",
      },
    ],
    localProof:
      "We have completed roof replacement work in Blaine, and we work across the surrounding north metro in Ham Lake, Coon Rapids, Anoka and Andover week to week.",
    faqs: [
      {
        question: "Do you offer free roofing and siding estimates in Blaine?",
        answer:
          "Yes. We provide free, no-obligation estimates for every Blaine homeowner. We'll inspect your roof, siding, or gutters, document any damage, and give you a clear written quote with no pressure.",
      },
      {
        question:
          "How quickly can you respond to storm or hail damage in Blaine?",
        answer:
          "Blaine is one of our most-served cities and sits in a hail-prone part of Anoka County, so storm response is something we handle constantly. After a major storm we prioritize damage inspections and can typically get a Blaine homeowner assessed and on the schedule quickly.",
      },
      {
        question: "Do you help Blaine homeowners with insurance claims?",
        answer:
          "Absolutely. A large share of the Blaine roofs and siding we replace are hail or wind claims. We document the damage, meet your adjuster on site, and make sure the scope reflects what your home actually needs—something we've done for Blaine homeowners for over 20 years.",
      },
      {
        question: "Are ice dams a problem on Blaine homes?",
        answer:
          "They can be, especially on homes with older or under-ventilated attics. Blaine's snowy winters and freeze-thaw swings are prime ice-dam conditions. We fix the damage and address the root cause—insulation, air sealing, and ventilation—so they're less likely to return.",
      },
      {
        question: "What siding holds up best in Blaine?",
        answer:
          "For Blaine's climate we most often recommend LP SmartSide engineered wood or James Hardie fiber cement for durability, with vinyl as a budget-friendly option. We'll match the look to your neighborhood and budget.",
      },
      {
        question: "How long does a roof replacement take in Blaine?",
        answer:
          "Most Blaine homes are completed in a single day; larger or more complex roofs may take two. We tear off, install a complete system, and finish with a thorough cleanup and magnet sweep.",
      },
      {
        question: "What areas near Blaine do you serve?",
        answer:
          "Along with Blaine, we regularly work in Coon Rapids, Anoka, Andover, Ham Lake, Lexington, Spring Lake Park, and across the north metro.",
      },
    ],
  },
  {
    name: "Fridley",
    slug: "fridley",
    description:
      "Trusted roofing and exterior contractor serving Fridley homes.",
    heroImage: "/og-image.png",
    localInfo: {
      about:
        "Fridley is a welcoming inner-ring suburb along the Mississippi River in Anoka County. With its convenient location, affordable housing, and strong community spirit, Fridley offers the best of suburban living with easy access to Minneapolis. We've been proud to serve Fridley homeowners for over 20 years.",
      landmarks: [
        "Springbrook Nature Center",
        "Locke County Park",
        "Islands of Peace County Park",
        "Moore Lake Park",
        "Commons Park",
      ],
      neighborhoods: [
        "Innsbruck",
        "Sylvan Hills",
        "Riverview Heights",
        "Hayes",
        "Rice Creek",
      ],
    },
    intro:
      "Fridley is an established inner-ring suburb along the Mississippi, with lots of mid-century ramblers and post-war homes—many with their original or aging roofs and siding. These homes are great candidates for a modern, energy-efficient exterior, and Fridley sees the same Minnesota hail, wind, and snow as its neighbors. We've been proud to serve Fridley homeowners for over 20 years.",
    housingStock:
      "Fridley is an inner-ring suburb and it looks it. The bulk of the city is post-war and mid-century: 1950s and 1960s ramblers and split-entries on modest lots, many with tuck-under or attached single-car garages and shallow attic space. These are simple roofs compared with the newer exurbs, mostly straightforward gables and hips, but the simplicity hides the two chronic problems of homes this age, which are thin attic insulation and low-slope sections over entries and garages. Original 5-inch gutters are common and undersized for the roof feeding them. Siding runs to aluminium and early vinyl, much of it long past the point where a matching repair panel exists.",
    exteriorChallenges: [
      {
        title: "Shallow attics and chronic ice dams",
        body: "A 1950s or 1960s rambler was rarely built with enough insulation, and the eaves in particular are often blocked or bare. Heat escapes, snow melts on the upper roof and refreezes at the cold overhang. Re-shingling alone does not touch this. Ice and water shield at the eaves, clear intake ventilation, air sealing and topped-up insulation are what actually stop the cycle.",
      },
      {
        title: "Low-slope entry and garage roofs",
        body: "Attached and tuck-under garages on these homes very often have a section below the minimum pitch for asphalt shingles. Shingled anyway, water works back under the tabs and shows up as a stain on the garage ceiling. Those areas need a membrane rather than another layer of shingle, and it is a distinction worth confirming in writing.",
      },
      {
        title: "Original gutters sized for a different era",
        body: "Five-inch gutter with one downspout was the default when these homes were built. Combined with ice dam conditions, the result is overflow in summer and ice in winter at the same corners year after year. Six-inch seamless with properly placed downspouts is a meaningful upgrade on a home this age.",
      },
      {
        title: "Aluminium siding with no matching parts",
        body: "Mid-century aluminium dents, chalks and fades, and matching profiles are no longer produced. Once a wall has taken hail or impact damage, patching is usually visible and rarely worth the money. Full replacement in engineered wood or fibre cement is where most Fridley homes end up, and it changes the look of the house entirely.",
      },
    ],
    faqs: [
      {
        question: "Do you offer free estimates in Fridley?",
        answer:
          "Yes—free, no-obligation estimates for every Fridley homeowner, with a clear written quote.",
      },
      {
        question: "How quickly can you respond to storm damage in Fridley?",
        answer:
          "Fridley is close to home for us, so we respond quickly after storms—prioritizing inspections and documenting any hail or wind damage.",
      },
      {
        question: "Do you help with insurance claims in Fridley?",
        answer:
          "Yes. We document the damage, meet your adjuster, and make sure your claim reflects the full scope of work needed.",
      },
      {
        question:
          "My mid-century Fridley home still has its original roof—should I replace it?",
        answer:
          "Many Fridley homes are well past the typical lifespan of their roofing and siding. We'll inspect honestly and tell you whether it's time, and which materials make sense for your home.",
      },
      {
        question: "Are ice dams common on Fridley homes?",
        answer:
          "Older and mid-century homes often have limited insulation and ventilation, which makes ice dams more likely. We fix the damage and correct the underlying causes.",
      },
      {
        question: "What siding works well on Fridley ramblers?",
        answer:
          "Ramblers and mid-century homes look great with clean LP SmartSide or modern vinyl. We'll help you choose a durable, low-maintenance option that fits your home and budget.",
      },
      {
        question: "What areas near Fridley do you serve?",
        answer:
          "Fridley plus Columbia Heights, Spring Lake Park, Coon Rapids, New Brighton, Mounds View, and the surrounding metro.",
      },
    ],
  },
];
